import { createRootRoute, createRoute, Outlet } from '@tanstack/react-router'
import { useLocation } from '@tanstack/react-router'
import { Layout } from '~/components/Layout'
import { HomePage } from '~/routes/home'
import { OrdersPage } from '~/routes/orders'
import { OrderNewPage } from '~/routes/order-new'
import { TrackingPage } from '~/routes/tracking'
import { AddressesPage } from '~/routes/addresses'
import { AccountPage } from '~/routes/account'
import { LoginPage } from '~/routes/login'
import { RegisterPage } from '~/routes/register'
import { ForgotPasswordPage } from '~/routes/forgot-password'
import { ResetPasswordPage } from '~/routes/reset-password'
import { createAdminRoutes } from '~/routes/admin'

// Full-screen branded auth pages render without the standard customer Layout.
const AUTH_ROUTES = new Set(['/login', '/register', '/forgot-password', '/reset-password'])

// Root component that conditionally renders Layout for non-admin/non-auth routes
function RootComponent() {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isAuthRoute = AUTH_ROUTES.has(location.pathname)

  // Admin routes handle their own layout (AdminLayout); auth pages are full-screen.
  if (isAdminRoute || isAuthRoute) {
    return <Outlet />
  }

  // Customer routes use the standard Layout
  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

const rootRoute = createRootRoute({
  component: RootComponent,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
})

const orderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order/new',
  component: OrderNewPage,
})

const trackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tracking/$orderId',
  component: TrackingPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      payment: search.payment as string | undefined,
    }
  },
})

const addressesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/addresses',
  component: AddressesPage,
})

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      reset: search.reset as string | undefined,
    }
  },
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: search.token as string | undefined,
    }
  },
})

// Create admin routes with role-based access guard
const adminRoutes = createAdminRoutes(rootRoute)

export const routeTree = rootRoute.addChildren([
  indexRoute,
  ordersRoute,
  orderNewRoute,
  trackingRoute,
  addressesRoute,
  accountRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  adminRoutes,
])
